// Bloco de "pular cálculo" das três telas que gravam o pulo: validação da fatura, Janela
// de Cobrança e acompanhamento do agrupamento. A regra mora em
// src/lib/pularCalculoInjecao.ts; aqui o componente carrega a fatura e a lista de usinas
// da Vigor, desenha os campos e devolve, por `onChange`, os campos prontos para gravar.
// O banco confere de novo (trg_validar_pulo_calculo).
//
// Com injeção sem usina da Vigor identificada, o analista digita o número de cada usina
// lendo o PDF. O número que o sistema leu só aparece depois que ele digita: a digitação é
// a conferência, e mostrar o lido antes viraria cópia.

import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, ExternalLink, Plus } from 'lucide-react';
import { supabase } from '../../../src/lib/supabase';
import { carregarUsinasVigor } from '../../../src/lib/usinasVigor';
import {
  MOTIVOS_PULAR_CALCULO,
  camposMotivoPularCalculo,
  exigeDescricaoPularCalculo,
  type CodigoMotivoPularCalculo,
} from '../../../src/lib/motivoPularCalculo';
import {
  classificarInjecao,
  formatarKwh,
  montarDetalhe,
  motivosPermitidos,
  validarPulo,
  type DetalhePulo,
  type FaturaInjecao,
  type ReferenciasPulo,
  type UsinaDigitada,
  type UsinasVigor,
} from '../../../src/lib/pularCalculoInjecao';

export interface CamposPulo {
  motivo_pular_calculo_codigo: CodigoMotivoPularCalculo;
  motivo_pular_calculo: string;
  pular_calculo_detalhe: DetalhePulo;
}

/** `campos` só vem preenchido quando tudo está válido; senão `erro` diz o que falta. */
export interface EstadoPulo {
  campos: CamposPulo | null;
  erro: string | null;
}

export const ESTADO_PULO_INICIAL: EstadoPulo = { campos: null, erro: 'Selecione o motivo.' };

interface Props {
  /** Fatura a pular. `null` quando a Janela vai criar a fatura já pulada (ela ainda não chegou). */
  faturaId: number | string | null | undefined;
  onChange: (estado: EstadoPulo) => void;
}

type FaturaCarregada = FaturaInjecao & {
  id: number;
  uc_codigo: string | null;
  uc_codigo_norm: string | null;
  pdf_fatura: string | null;
};

interface FaturaComBoleto { id: number; mes_referencia: string | null; num_cobranca_boleto: string | null }
interface Acordo { id: string; status: string | null; created_at: string; total_acordo: number | null }
interface Autorizador { id: string; full_name: string | null; role: string }

const COLUNAS_FATURA = [
  'id', 'uc_codigo', 'uc_codigo_norm', 'pdf_fatura', 'energia_injetada_gd1_kwh', 'energia_injetada_gd2_kwh',
  ...[1, 2, 3, 4, 5].flatMap((i) => [`unidade_geradora_${i}`, `injecao_kwh_ug_${i}`]),
].join(', ');

const SEM_USINAS: UsinasVigor = new Map();

const ESTILO_CAMPO = 'w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-3 text-sm text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary';
const ESTILO_ROTULO = 'block text-xs font-bold text-gray-400 uppercase mb-1';

const reais = (v: number | null) =>
  v == null ? '' : v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const PularCalculoCampo: React.FC<Props> = ({ faturaId, onChange }) => {
  const [fatura, setFatura] = useState<FaturaCarregada | null>(null);
  const [usinas, setUsinas] = useState<UsinasVigor | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erroCarga, setErroCarga] = useState<string | null>(null);

  const [codigo, setCodigo] = useState<CodigoMotivoPularCalculo | ''>('');
  const [descricao, setDescricao] = useState('');
  const [digitados, setDigitados] = useState<Record<number, string>>({});
  const [extras, setExtras] = useState<string[]>(['']);
  const [conferidos, setConferidos] = useState<Set<number>>(new Set());
  const [confirmouDivergencia, setConfirmouDivergencia] = useState(false);
  const [faturaRefId, setFaturaRefId] = useState('');
  const [parcelamentoId, setParcelamentoId] = useState('');
  const [autorizadoPorId, setAutorizadoPorId] = useState('');

  const [faturasComBoleto, setFaturasComBoleto] = useState<FaturaComBoleto[]>([]);
  const [acordos, setAcordos] = useState<Acordo[]>([]);
  const [autorizadores, setAutorizadores] = useState<Autorizador[]>([]);

  // Carga: só as colunas da regra, e a lista de usinas da Vigor.
  useEffect(() => {
    let vivo = true;
    (async () => {
      setCarregando(true);
      setErroCarga(null);
      try {
        const [lista, f] = await Promise.all([
          carregarUsinasVigor(),
          faturaId == null || faturaId === ''
            ? Promise.resolve(null)
            : supabase.from('faturas').select(COLUNAS_FATURA).eq('id', Number(faturaId)).maybeSingle()
              .then(({ data, error }) => {
                if (error) throw error;
                return data as unknown as FaturaCarregada | null;
              }),
        ]);
        if (!vivo) return;
        setUsinas(lista);
        setFatura(f);
      } catch (e: any) {
        if (vivo) setErroCarga(`Não foi possível carregar a fatura: ${e?.message || e}`);
      } finally {
        if (vivo) setCarregando(false);
      }
    })();
    return () => { vivo = false; };
  }, [faturaId]);

  const listaUsinas = usinas ?? SEM_USINAS;
  const digitadas: UsinaDigitada[] = useMemo(() => [
    ...Object.entries(digitados).map(([linha, numero]) => ({ linha: Number(linha), numero })),
    ...extras.map((numero) => ({ linha: null, numero })),
  ], [digitados, extras]);
  // `base` decide se o analista precisa digitar; `final` já considera o que ele digitou.
  const base = useMemo(() => classificarInjecao(fatura ?? {}, listaUsinas), [fatura, listaUsinas]);
  const final = useMemo(() => classificarInjecao(fatura ?? {}, listaUsinas, digitadas), [fatura, listaUsinas, digitadas]);
  const permitidos = motivosPermitidos(final.situacao);
  const semListaUsinas = !carregando && base.kwh_total > 0 && !usinas;

  // Motivo escolhido que deixou de valer (ex.: o número digitado era de usina da Vigor).
  useEffect(() => {
    if (codigo && !permitidos.includes(codigo)) setCodigo('');
  }, [final.situacao]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (codigo !== 'boleto_ja_enviado' || !fatura?.uc_codigo_norm) return;
    let vivo = true;
    supabase.from('faturas').select('id, mes_referencia, num_cobranca_boleto')
      .eq('uc_codigo_norm', fatura.uc_codigo_norm)
      .not('num_cobranca_boleto', 'is', null)
      .neq('status_fatura', 'Excluído')
      .neq('id', fatura.id)
      .order('id', { ascending: false })
      .limit(24)
      .then(({ data }) => { if (vivo) setFaturasComBoleto((data as FaturaComBoleto[]) || []); });
    return () => { vivo = false; };
  }, [codigo, fatura]);

  useEffect(() => {
    if (codigo !== 'saldo_em_acordo' || !fatura?.uc_codigo_norm) return;
    let vivo = true;
    (async () => {
      const norm = fatura.uc_codigo_norm!;
      const { data: ucs } = await supabase.from('unidades_consumidoras').select('id')
        .or(`numero_uc_norm.eq.${norm},outro_numero_uc_norm.eq.${norm}`);
      const ids = (ucs || []).map((u: { id: number }) => u.id);
      if (!ids.length) { if (vivo) setAcordos([]); return; }
      const { data } = await supabase.from('parcelamentos').select('id, status, created_at, total_acordo')
        .in('unidade_consumidora_id', ids)
        .order('created_at', { ascending: false });
      if (vivo) setAcordos((data as Acordo[]) || []);
    })();
    return () => { vivo = false; };
  }, [codigo, fatura]);

  useEffect(() => {
    if (codigo !== 'dispensa_autorizada' || autorizadores.length) return;
    let vivo = true;
    supabase.from('profiles').select('id, full_name, role')
      .in('role', ['adm', 'gerente'])
      .eq('status', 'active')
      .order('full_name')
      .then(({ data }) => { if (vivo) setAutorizadores((data as Autorizador[]) || []); });
    return () => { vivo = false; };
  }, [codigo, autorizadores.length]);

  const autorizador = autorizadores.find((a) => a.id === autorizadoPorId) ?? null;
  const refs: ReferenciasPulo = {
    fatura_ref_id: faturaRefId ? Number(faturaRefId) : null,
    parcelamento_id: parcelamentoId || null,
    autorizado_por_id: autorizadoPorId || null,
    autorizado_por_nome: autorizador?.full_name ?? null,
    confirmou_divergencia: confirmouDivergencia,
  };

  const erro = carregando
    ? 'Carregando a fatura...'
    : erroCarga
      ?? (semListaUsinas ? 'Não foi possível carregar a lista de usinas da Vigor. Feche e abra de novo; se continuar, avise a TI.' : null)
      ?? validarPulo(final, codigo || null, descricao, refs);

  // Texto legível gravado em motivo_pular_calculo: rótulo + o que identifica a escolha.
  const complemento = () => {
    const partes: string[] = [];
    if (codigo === 'boleto_ja_enviado' && refs.fatura_ref_id) partes.push(`fatura #${refs.fatura_ref_id}`);
    if (codigo === 'saldo_em_acordo' && refs.parcelamento_id) partes.push(`acordo ${String(refs.parcelamento_id).slice(0, 8)}`);
    if (codigo === 'injecao_usina_terceiro') {
      const numeros = final.usinas.map((u) => u.numero_digitado).filter(Boolean);
      if (numeros.length) partes.push(`usina(s) ${numeros.join(', ')}`);
    }
    if (descricao.trim()) partes.push(descricao.trim());
    if (codigo === 'dispensa_autorizada' && refs.autorizado_por_nome) partes.push(`autorizado por ${refs.autorizado_por_nome}`);
    return partes.join(' — ');
  };

  const campos: CamposPulo | null = erro || !codigo ? null : {
    ...camposMotivoPularCalculo(codigo, complemento()),
    pular_calculo_detalhe: montarDetalhe(final, refs, descricao),
  };

  const assinatura = JSON.stringify([erro, campos]);
  useEffect(() => {
    onChange({ campos, erro });
  }, [assinatura]); // eslint-disable-line react-hooks/exhaustive-deps

  if (carregando) {
    return <p className="text-sm text-gray-500 dark:text-gray-400 text-left">Carregando a fatura...</p>;
  }

  const linhasParaDigitar = base.situacao === 'B' ? base.usinas.filter((u) => u.linha !== null) : [];
  const descricaoObrigatoria = exigeDescricaoPularCalculo(codigo);
  // Campo que o motivo pede ao lado dele (fatura do boleto, acordo ou quem autorizou).
  const temCampoDoMotivo = (codigo === 'boleto_ja_enviado' && final.situacao === 'C')
    || codigo === 'saldo_em_acordo' || codigo === 'dispensa_autorizada';

  const avisoUsinaVigor = final.situacao === 'C' && (
    <div className="flex gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-200">
      <AlertTriangle size={18} className="mt-0.5 shrink-0" />
      <p>
        Esta conta tem <strong>{formatarKwh(final.kwh_vigor)} kWh</strong> de injeção da <strong>{final.usina_vigor}</strong>,
        usina da Vigor{base.situacao === 'B' ? ' (identificada pelo número que você digitou)' : ''}. Só os motivos abaixo são aceitos.
      </p>
    </div>
  );

  return (
    <div className="space-y-3 text-left">
      {(erroCarga || semListaUsinas) && (
        <p role="alert" className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-300">
          {erroCarga || 'Não foi possível carregar a lista de usinas da Vigor. Feche e abra de novo; se continuar, avise a TI.'}
        </p>
      )}

      {/* Usina da Vigor já lida pelo sistema: o aviso vem antes de tudo. Se ela só aparece
          pelo número digitado, o aviso fica depois das usinas, junto do motivo que ele limita,
          e não empurra para baixo o campo que a pessoa está digitando. */}
      {base.situacao !== 'B' && avisoUsinaVigor}

      {base.situacao === 'B' && (
        <div className="space-y-3 rounded-xl border border-amber-200 bg-amber-50 p-3 dark:border-amber-900/40 dark:bg-amber-900/10">
          <p className="text-sm text-amber-900 dark:text-amber-200">
            Esta conta tem <strong>{formatarKwh(base.kwh_total)} kWh</strong> de injeção e nenhuma usina da Vigor foi identificada.
            Abra o PDF e digite o número de cada usina, do jeito que está na conta.
          </p>
          {fatura?.pdf_fatura && (
            <a href={fatura.pdf_fatura} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline">
              <ExternalLink size={14} /> Abrir PDF da conta
            </a>
          )}

          {linhasParaDigitar.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {linhasParaDigitar.map((u) => {
            const linha = u.linha as number;
            const valor = digitados[linha] ?? '';
            const resultado = final.usinas.find((x) => x.linha === linha);
            const mostrar = conferidos.has(linha) && !!valor.trim() && resultado;
            return (
              <div key={linha}>
                <label htmlFor={`usina-linha-${linha}`} className={ESTILO_ROTULO}>
                  Usina da linha {linha} · {formatarKwh(u.kwh ?? 0)} kWh
                </label>
                <input
                  id={`usina-linha-${linha}`}
                  value={valor}
                  inputMode="numeric"
                  autoComplete="off"
                  placeholder="Número da usina, como está no PDF"
                  onChange={(e) => {
                    const v = e.target.value;
                    setDigitados((d) => ({ ...d, [linha]: v }));
                    setConfirmouDivergencia(false);
                  }}
                  onBlur={() => setConferidos((s) => new Set(s).add(linha))}
                  className={ESTILO_CAMPO}
                />
                {mostrar && (
                  resultado.usina_vigor ? (
                    <p className="mt-1 text-xs font-semibold text-red-700 dark:text-red-300">Este número é da {resultado.usina_vigor}, usina da Vigor.</p>
                  ) : !resultado.numero_lido ? (
                    <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">O sistema não tinha lido o número desta linha. Não é usina da Vigor.</p>
                  ) : resultado.divergente ? (
                    <p className="mt-1 text-xs font-semibold text-amber-800 dark:text-amber-300">O sistema leu {resultado.numero_lido}. Confira no PDF qual está certo.</p>
                  ) : (
                    <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">Confere com a leitura do sistema. Não é usina da Vigor.</p>
                  )
                )}
              </div>
            );
          })}
          </div>
          )}

          {base.kwh_sem_usina_lida > 0 && (
            <div className="space-y-2">
              <p className={ESTILO_ROTULO}>{formatarKwh(base.kwh_sem_usina_lida)} kWh sem usina lida pelo sistema</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {extras.map((v, i) => (
                <input
                  key={i}
                  aria-label={`Usina ${i + 1} dos kWh sem usina lida`}
                  value={v}
                  inputMode="numeric"
                  autoComplete="off"
                  placeholder="Número da usina, como está no PDF"
                  onChange={(e) => {
                    const novo = e.target.value;
                    setExtras((xs) => xs.map((x, j) => (j === i ? novo : x)));
                  }}
                  className={ESTILO_CAMPO}
                />
              ))}
              </div>
              <button type="button" onClick={() => setExtras((xs) => [...xs, ''])} className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline">
                <Plus size={14} /> Adicionar usina
              </button>
            </div>
          )}

          {final.usinas.some((u) => u.divergente) && final.situacao === 'B' && (
            <label className="flex items-start gap-2 text-sm text-amber-900 dark:text-amber-200">
              <input type="checkbox" checked={confirmouDivergencia} onChange={(e) => setConfirmouDivergencia(e.target.checked)} className="mt-1" />
              Conferi no PDF: o número certo é o que digitei.
            </label>
          )}
        </div>
      )}

      {base.situacao === 'B' && avisoUsinaVigor}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <div className={temCampoDoMotivo ? '' : 'sm:col-span-2'}>
        <label htmlFor="motivo-pular-calculo" className={ESTILO_ROTULO}>
          Motivo <span className="text-red-500">*</span>
        </label>
        <select
          id="motivo-pular-calculo"
          value={codigo}
          onChange={(e) => setCodigo(e.target.value as CodigoMotivoPularCalculo | '')}
          className={ESTILO_CAMPO}
        >
          <option value="">Selecione o motivo...</option>
          {MOTIVOS_PULAR_CALCULO.filter((m) => permitidos.includes(m.codigo)).map((m) => (
            <option key={m.codigo} value={m.codigo}>{m.rotulo}</option>
          ))}
        </select>
      </div>

      {codigo === 'boleto_ja_enviado' && final.situacao === 'C' && (
        <div>
          <label htmlFor="pulo-fatura-ref" className={ESTILO_ROTULO}>Fatura que já tem o boleto <span className="text-red-500">*</span></label>
          <select id="pulo-fatura-ref" value={faturaRefId} onChange={(e) => setFaturaRefId(e.target.value)} className={ESTILO_CAMPO}>
            <option value="">{faturasComBoleto.length ? 'Escolha a fatura...' : 'Nenhuma fatura desta UC com boleto'}</option>
            {faturasComBoleto.map((f) => (
              <option key={f.id} value={f.id}>#{f.id} · {f.mes_referencia || 'sem mês'} · boleto {f.num_cobranca_boleto}</option>
            ))}
          </select>
        </div>
      )}

      {codigo === 'saldo_em_acordo' && (
        <div>
          <label htmlFor="pulo-acordo" className={ESTILO_ROTULO}>Acordo em que o saldo foi incluído <span className="text-red-500">*</span></label>
          <select id="pulo-acordo" value={parcelamentoId} onChange={(e) => setParcelamentoId(e.target.value)} className={ESTILO_CAMPO}>
            <option value="">{acordos.length ? 'Escolha o acordo...' : 'Nenhum acordo desta UC'}</option>
            {acordos.map((a) => (
              <option key={a.id} value={a.id}>
                {new Date(a.created_at).toLocaleDateString('pt-BR')} · {a.status || 'sem status'}{a.total_acordo != null ? ` · ${reais(a.total_acordo)}` : ''}
              </option>
            ))}
          </select>
        </div>
      )}

      {codigo === 'dispensa_autorizada' && (
        <div>
          <label htmlFor="pulo-autorizador" className={ESTILO_ROTULO}>Quem autorizou <span className="text-red-500">*</span></label>
          <select id="pulo-autorizador" value={autorizadoPorId} onChange={(e) => setAutorizadoPorId(e.target.value)} className={ESTILO_CAMPO}>
            <option value="">Escolha gerente ou adm...</option>
            {autorizadores.map((a) => (
              <option key={a.id} value={a.id}>{a.full_name || a.id} ({a.role})</option>
            ))}
          </select>
        </div>
      )}
      </div>

      <div>
        <label htmlFor="motivo-pular-calculo-descricao" className={ESTILO_ROTULO}>
          {codigo === 'dispensa_autorizada' ? 'Por que a cobrança foi dispensada' : 'Descrição'}{' '}
          {descricaoObrigatoria ? <span className="text-red-500">*</span> : <span className="normal-case font-normal">(opcional)</span>}
        </label>
        <textarea
          id="motivo-pular-calculo-descricao"
          value={descricao}
          onChange={(e) => setDescricao(e.target.value)}
          placeholder={descricaoObrigatoria ? 'Descreva o motivo...' : 'Detalhe, documento ou contato que sustenta o motivo'}
          rows={3}
          className={`${ESTILO_CAMPO} min-h-[72px] resize-y`}
        />
      </div>

      {codigo && erro && (
        <p role="alert" className="text-xs font-medium text-red-700 dark:text-red-300">{erro}</p>
      )}
    </div>
  );
};

export default PularCalculoCampo;
